import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowTopRightOnSquareIcon,
  DevicePhoneMobileIcon,
  LifebuoyIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../providers/auth-provider';
import { useApiClient } from '../../lib/api';
import DownloadQrCode from './DownloadQrCode';
import { DOWNLOAD_OPTION_KEYS, pickPrimaryDownloadOption } from './downloadOptions';

export default function MobileDownloadFirstAccessDialog({ open }) {
  const navigate = useNavigate();
  const api = useApiClient();
  const { acknowledgeMobileDownloadNotice } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [downloadOption, setDownloadOption] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;

    const loadDownloadInfo = async () => {
      setDownloadLoading(true);
      setDownloadError(false);

      try {
        const response = await api.get('/api/v1/help');
        if (cancelled) {
          return;
        }

        setDownloadOption(pickPrimaryDownloadOption(response.data?.downloads));
      } catch {
        if (!cancelled) {
          setDownloadOption(null);
          setDownloadError(true);
        }
      } finally {
        if (!cancelled) {
          setDownloadLoading(false);
        }
      }
    };

    loadDownloadInfo();

    return () => {
      cancelled = true;
    };
  }, [api, open]);

  const isPlayStoreDownload = downloadOption?.key === DOWNLOAD_OPTION_KEYS.playStore;
  const qrLabel = downloadOption ? `QR Code - ${downloadOption.label}` : 'QR Code de download do app';
  const qrDescription = downloadOption?.description ?? 'Escaneie para abrir o download no dispositivo Android.';
  const downloadGuidance = useMemo(() => {
    if (downloadLoading) {
      return 'Estamos carregando o link de download disponível para este ambiente.';
    }

    if (downloadError) {
      return 'Se o QR Code não carregar agora, acesse Ajuda & Suporte pelo menu lateral e use o botão de download.';
    }

    if (isPlayStoreDownload) {
      return 'Use a Play Store como caminho principal de instalação e mantenha notificações habilitadas no Android.';
    }

    return 'Use o download direto do APK e revise as permissões indicadas, principalmente sobreposição e bateria.';
  }, [downloadError, downloadLoading, isPlayStoreDownload]);

  const acknowledge = async ({ goToDownloads = false } = {}) => {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await acknowledgeMobileDownloadNotice();
      if (goToDownloads) {
        navigate('/app/help#mobile-downloads');
      }
    } catch {
      setError('Não foi possível registrar este aviso agora. Verifique a conexão e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[120]" onClose={() => acknowledge()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-left sm:items-center sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="translate-y-4 opacity-0 sm:translate-y-0 sm:scale-95"
              enterTo="translate-y-0 opacity-100 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="translate-y-0 opacity-100 sm:scale-100"
              leaveTo="translate-y-4 opacity-0 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-900/10">
                <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                        <DevicePhoneMobileIcon className="h-6 w-6" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <Dialog.Title className="text-lg font-semibold tracking-tight text-slate-950">
                          Instale o app da equipe
                        </Dialog.Title>
                        <p className="mt-1 text-sm leading-6 text-slate-600">
                          O link de download e as permissões de instalação ficam em Ajuda & Suporte, na seção Aplicativo.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => acknowledge()}
                      disabled={submitting}
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Fechar aviso"
                    >
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="px-5 py-5 sm:px-6">
                  <DownloadQrCode
                    url={downloadOption?.url}
                    label={qrLabel}
                    description={qrDescription}
                    loading={downloadLoading}
                    error={downloadError}
                  />

                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <LifebuoyIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                      <p className="mt-3 text-sm font-semibold text-slate-950">Menu lateral</p>
                      <p className="mt-1 text-sm leading-6 text-slate-600">Acesse Ajuda & Suporte sempre que precisar instalar outro dispositivo.</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <DevicePhoneMobileIcon className="h-5 w-5 text-slate-700" aria-hidden="true" />
                      <p className="mt-3 text-sm font-semibold text-slate-950">{downloadOption?.label ?? 'Download do app'}</p>
                      <p className="mt-1 text-sm leading-6 text-slate-600">{downloadGuidance}</p>
                    </div>
                  </div>

                  {error ? (
                    <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700">
                      {error}
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
                  <button
                    type="button"
                    onClick={() => acknowledge()}
                    disabled={submitting}
                    className="inline-flex justify-center rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Ver depois
                  </button>
                  <button
                    type="button"
                    onClick={() => acknowledge({ goToDownloads: true })}
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Ir para download do app
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
